// Central data store for every company that has a full review page.
// Each entry follows the same shape as the original EVRi mock so the
// existing components can render it without changes.

const tagsForCategory = {
  logistics: ["parcel", "delivery", "service", "tracking", "customer", "address", "package", "customer service", "email", "refund"],
  telecoms: ["bill", "broadband", "signal", "contract", "router", "customer service", "cancel", "price rise", "engineer", "speed"],
  retail: ["order", "delivery", "refund", "stock", "click & collect", "customer service", "return", "price", "staff", "app"],
  fashion: ["order", "size", "return", "refund", "delivery", "fit", "quality", "customer service", "exchange", "tracking"],
  utilities: ["bill", "meter", "engineer", "tariff", "smart meter", "customer service", "credit", "ombudsman", "direct debit", "transfer"],
  tv: ["bill", "router", "signal", "subscription", "package", "engineer", "customer service", "cancel", "price rise", "remote"],
};

// Shared takeaway helper
const tk = (industry, what) => ({
  key: [
    `Expect long waits for ${what} resolution and limited human contact.`,
    "Document every interaction with timestamps and reference numbers.",
    "Consider alternatives if reliability matters more than price.",
  ],
  negative: [
    `Frequent reports of ${what} issues that go unresolved for weeks.`,
    "Customer service routed through bots that close cases prematurely.",
    "Refund and complaint processes are slow and require persistence.",
  ],
  positive: `Some customers report positive experiences with individual ${industry} staff and competitive pricing, but the overall picture is mixed.`,
});

export const COMPANIES = {
  evri: {
    slug: "evri",
    name: "EVRi",
    url: "www.evri.com",
    logo: null,
    category: "Transportation & Logistics",
    color: "#E50051",
    initials: "EV",
    rating: 1.3,
    totalReviews: 3422,
    ratedReviews: 3417,
    recommend: 4,
    negative: 94,
    positive: 2,
    updatedOn: "05/29/2026",
    ratingBreakdown: { 5: 80, 4: 89, 3: 158, 2: 435, 1: 4147 },
    breadcrumbCategory: ["Transportation and Logistics", "/browse/transportation"],
    details: {
      legalName: "Evri Limited (formerly Hermes UK)",
      industry: "Transportation & Logistics — Parcel Delivery",
      founded: "1974",
      headquarters: "Capitol House, 1 Capitol Close, Morley, Leeds, LS27 0WH, United Kingdom",
      ceo: "Martijn de Lange",
      employees: "30,000+ (incl. self-employed couriers)",
      parcelsPerYear: "Over 730 million annually",
      website: "https://www.evri.com",
      trackingUrl: "https://www.evri.com/track-a-parcel",
      customerSupport: {
        phone: "+44 330 808 5456",
        hours: "Mon–Fri 8:00–18:00 · Sat 9:00–17:00 · Sun 10:00–16:00",
        chat: "24/7 AI Assistant via evri.com/contact-us",
        email: "customerservices@evri.com",
        twitter: "@EvriHelp",
        facebook: "facebook.com/Evri.UK",
      },
      postalAddress: "Evri Customer Services, PO Box 3037, Liverpool, L70 1JN",
      services: [
        "Parcel delivery (next day / standard)",
        "ParcelShop drop-off & collection",
        "International shipping",
        "Returns service for retailers (Vinted, eBay, ASOS, etc.)",
        "Business / B2C shipping solutions",
      ],
      popularSearches: [
        "Evri tracking", "Evri customer service number", "Evri lost parcel claim",
        "Evri ParcelShop near me", "Evri delivery times", "Evri complaints",
      ],
      introBlurb: "EVRi (formerly Hermes UK) is one of the largest parcel-delivery companies in the United Kingdom, handling hundreds of millions of consumer and B2C parcels each year through a network of self-employed couriers and over 30,000 high-street ParcelShop drop-off and collection points. It is the default returns courier for major marketplaces including Vinted, eBay and ASOS.",
    },
    tags: tagsForCategory.logistics,
    takeaways: {
      key: [
        "Expect recurring customer complaints about delivery reliability and inability to contact a human.",
        "Consider alternatives if timely delivery or handling of valuable items matters.",
        "Track parcels closely and document issues for refunds or claims.",
      ],
      negative: [
        "Frequent reports of lost, stolen, or misdelivered parcels and false proof-of-delivery photos.",
        "Poor customer service access; bots and delayed responses hinder refunds and claims.",
        "Unreliable scheduling and repeated missed windows affect recipients and senders.",
      ],
      positive: "Some reviews note low price, but overall few pros are reported and most users advise avoiding EVRi based on delivery and service experiences.",
    },
    // Reviews live in a separate file to keep this readable
  },

  "royal-mail": {
    slug: "royal-mail",
    name: "Royal Mail",
    url: "www.royalmail.com",
    category: "Transportation & Logistics",
    color: "#DC0000",
    initials: "RM",
    rating: 1.5,
    totalReviews: 2840,
    ratedReviews: 2840,
    recommend: 12,
    negative: 81,
    positive: 8,
    updatedOn: "05/28/2026",
    ratingBreakdown: { 5: 230, 4: 198, 3: 312, 2: 540, 1: 1560 },
    breadcrumbCategory: ["Transportation and Logistics", "/browse/transportation"],
    details: {
      legalName: "Royal Mail Group Ltd (International Distributions Services plc)",
      industry: "Postal Service — Letters & Parcels",
      founded: "1516",
      headquarters: "185 Farringdon Road, London, EC1A 1AA, United Kingdom",
      ceo: "Emma Gilthorpe",
      employees: "115,000+",
      parcelsPerYear: "Over 1.2 billion letters and 500 million parcels",
      website: "https://www.royalmail.com",
      trackingUrl: "https://www.royalmail.com/track-your-item",
      customerSupport: {
        phone: "+44 345 774 0740",
        hours: "Mon–Fri 8:00–18:00 · Sat 8:00–13:00",
        chat: "Online chat via royalmail.com/help",
        email: "customer.service@royalmail.com",
        twitter: "@RoyalMailHelp",
        facebook: "facebook.com/RoyalMailGroup",
      },
      postalAddress: "Royal Mail Customer Services, PO Box 740, Plymouth, PL9 7YB",
      services: [
        "First & Second Class letters and parcels",
        "Signed For and Special Delivery Guaranteed",
        "Tracked 24 / Tracked 48",
        "International standard and tracked",
        "Click & Drop business postage online",
      ],
      popularSearches: [
        "Royal Mail tracking", "Royal Mail strike updates", "Royal Mail customs charge",
        "Redelivery booking", "Royal Mail compensation claim", "Royal Mail price list",
      ],
      introBlurb: "Royal Mail is the United Kingdom's universal postal service operator, delivering letters and parcels to every UK address six days a week. Founded over 500 years ago, it employs more than 115,000 people and handles more than a billion items each year.",
    },
    tags: tagsForCategory.logistics,
    takeaways: tk("postal", "delayed mail and missing parcels"),
  },

  dpd: {
    slug: "dpd",
    name: "DPD",
    url: "www.dpd.co.uk",
    category: "Transportation & Logistics",
    color: "#DC0000",
    initials: "DPD",
    rating: 1.8,
    totalReviews: 1956,
    ratedReviews: 1950,
    recommend: 22,
    negative: 70,
    positive: 18,
    updatedOn: "05/27/2026",
    ratingBreakdown: { 5: 280, 4: 220, 3: 240, 2: 380, 1: 836 },
    breadcrumbCategory: ["Transportation and Logistics", "/browse/transportation"],
    details: {
      legalName: "DPD Group UK Ltd (Geopost)",
      industry: "Transportation & Logistics — Parcel Delivery",
      founded: "1970 (as Parceline)",
      headquarters: "Roebuck Lane, Smethwick, West Midlands, B66 1BY, United Kingdom",
      ceo: "Elaine Kerr",
      employees: "15,000+",
      parcelsPerYear: "Over 400 million annually",
      website: "https://www.dpd.co.uk",
      trackingUrl: "https://www.dpd.co.uk/tracking",
      customerSupport: {
        phone: "+44 121 275 0500",
        hours: "Mon–Fri 8:00–20:00 · Sat 8:30–18:00 · Sun 9:00–17:00",
        chat: "DPD App chat 7 days a week",
        email: "customerservice@dpd.co.uk",
        twitter: "@DPDgroup_news",
        facebook: "facebook.com/DPDUK",
      },
      postalAddress: "DPD Customer Services, PO Box 6911, Birmingham, B66 1BY",
      services: [
        "Predict 1-hour delivery slot",
        "Next-day, evening & Saturday delivery",
        "Pickup shop network",
        "International express",
        "B2B and same-day options",
      ],
      popularSearches: [
        "DPD predict slot change", "DPD tracking", "DPD pickup shops near me",
        "DPD complaint", "DPD missed delivery", "DPD app login",
      ],
      introBlurb: "DPD (part of Geopost) is one of the UK's largest premium-tier parcel carriers, best known for its 1-hour Predict delivery window and driver-photo proof of delivery. It handles over 400 million parcels per year through 70+ UK depots.",
    },
    tags: tagsForCategory.logistics,
    takeaways: tk("courier", "delivery rescheduling and missed slots"),
  },

  yodel: {
    slug: "yodel",
    name: "Yodel",
    url: "www.yodel.co.uk",
    category: "Transportation & Logistics",
    color: "#0066B3",
    initials: "Y",
    rating: 1.2,
    totalReviews: 3201,
    ratedReviews: 3201,
    recommend: 3,
    negative: 92,
    positive: 3,
    updatedOn: "05/26/2026",
    ratingBreakdown: { 5: 60, 4: 70, 3: 150, 2: 420, 1: 2501 },
    breadcrumbCategory: ["Transportation and Logistics", "/browse/transportation"],
    details: {
      legalName: "Yodel Delivery Network Ltd",
      industry: "Transportation & Logistics — Parcel Delivery",
      founded: "2010",
      headquarters: "Sandon Building, Skyways Drive, Speke, Liverpool, L70 1AB",
      ceo: "Mike Hancox",
      employees: "10,000+",
      parcelsPerYear: "Over 190 million annually",
      website: "https://www.yodel.co.uk",
      trackingUrl: "https://www.yodel.co.uk/tracking",
      customerSupport: {
        phone: "+44 800 188 4682 (limited hours)",
        hours: "Mon–Fri 8:00–18:00 · Sat 9:00–17:00",
        chat: "Yodel chat at yodel.co.uk/help",
        email: "Submitted via web form only",
        twitter: "@YodelOnline",
        facebook: "facebook.com/YodelOnline",
      },
      postalAddress: "Yodel Customer Services, Sandon Building, Skyways Drive, L70 1AB",
      services: [
        "Standard 2-3 day parcel delivery",
        "Next-day & Sunday delivery",
        "Direct & Direct XL for retailers",
        "Returns service",
        "Click & Collect through partner stores",
      ],
      popularSearches: [
        "Yodel tracking", "Yodel complaint", "Yodel lost parcel claim",
        "Yodel customer service number", "Yodel delivery times", "Yodel app",
      ],
      introBlurb: "Yodel is a UK-only parcel-delivery company spun out of Home Delivery Network in 2010. It carries roughly 190 million parcels each year for retailers including Argos, JD Sports and Boots, and is consistently rated near the bottom of UK courier surveys.",
    },
    tags: tagsForCategory.logistics,
    takeaways: tk("courier", "lost parcels and uncontactable customer service"),
  },

  parcelforce: {
    slug: "parcelforce",
    name: "Parcelforce",
    url: "www.parcelforce.com",
    category: "Transportation & Logistics",
    color: "#DC0000",
    initials: "PF",
    rating: 1.6,
    totalReviews: 1245,
    ratedReviews: 1245,
    recommend: 14,
    negative: 76,
    positive: 12,
    updatedOn: "05/25/2026",
    ratingBreakdown: { 5: 110, 4: 90, 3: 130, 2: 240, 1: 675 },
    breadcrumbCategory: ["Transportation and Logistics", "/browse/transportation"],
    details: {
      legalName: "Parcelforce Worldwide (Royal Mail Group Ltd)",
      industry: "Transportation & Logistics — Express Parcels",
      founded: "1990",
      headquarters: "25 Caldecotte Lake Drive, Milton Keynes, MK7 8LE",
      ceo: "Stuart Forsyth",
      employees: "5,500+",
      parcelsPerYear: "Approximately 90 million",
      website: "https://www.parcelforce.com",
      trackingUrl: "https://www.parcelforce.com/track-trace",
      customerSupport: {
        phone: "+44 344 800 4466",
        hours: "Mon–Fri 8:00–19:00 · Sat 8:30–17:00",
        chat: "Available via parcelforce.com/help",
        email: "customer.service@parcelforce.co.uk",
        twitter: "@parcelforce",
        facebook: "facebook.com/parcelforce",
      },
      postalAddress: "Parcelforce Customer Service, PO Box 491, Plymouth, PL9 7YB",
      services: [
        "Express24, Express48 and Express AM",
        "International Datapost",
        "Worldwide tracked services",
        "Heavy and large parcels (up to 30kg)",
        "Post Office drop-off",
      ],
      popularSearches: [
        "Parcelforce tracking", "Parcelforce customs charge", "Parcelforce complaint",
        "Parcelforce depot location", "Parcelforce delivery times", "Parcelforce VAT charge",
      ],
      introBlurb: "Parcelforce Worldwide is the express-parcels arm of Royal Mail Group, focused on time-critical and international shipments. Customers often contact them about customs/VAT 'handling' fees on items shipped from outside the UK.",
    },
    tags: tagsForCategory.logistics,
    takeaways: tk("courier", "customs handling fees and missed Express24 windows"),
  },

  ups: {
    slug: "ups",
    name: "UPS",
    url: "www.ups.com/gb",
    category: "Transportation & Logistics",
    color: "#361C0C",
    initials: "UPS",
    rating: 2.1,
    totalReviews: 4520,
    ratedReviews: 4520,
    recommend: 26,
    negative: 64,
    positive: 22,
    updatedOn: "05/24/2026",
    ratingBreakdown: { 5: 700, 4: 540, 3: 620, 2: 880, 1: 1780 },
    breadcrumbCategory: ["Transportation and Logistics", "/browse/transportation"],
    details: {
      legalName: "United Parcel Service of America, Inc. (UK branch)",
      industry: "Transportation & Logistics — International Express",
      founded: "1907 (UK operations from 1989)",
      headquarters: "Forest Road, Feltham, Middlesex, TW13 7DT",
      ceo: "Carol B. Tomé (Global)",
      employees: "10,000+ in the UK",
      parcelsPerYear: "Approximately 6 billion globally",
      website: "https://www.ups.com/gb",
      trackingUrl: "https://www.ups.com/track",
      customerSupport: {
        phone: "+44 345 787 7877",
        hours: "Mon–Fri 7:30–20:00 · Sat 8:30–13:00",
        chat: "24/7 chat via ups.com/help",
        email: "ukcustomerservice@ups.com",
        twitter: "@UPSHelp",
        facebook: "facebook.com/ups",
      },
      postalAddress: "UPS Ltd, Forest Road, Feltham, Middlesex, TW13 7DT",
      services: [
        "UPS Worldwide Express",
        "UPS Standard (Europe road)",
        "Domestic UK Express",
        "Returns & SurePost",
        "Customs brokerage",
      ],
      popularSearches: [
        "UPS tracking", "UPS access point", "UPS customs invoice", "UPS UK contact",
        "UPS delivery missed", "UPS brokerage fee",
      ],
      introBlurb: "UPS (United Parcel Service) is one of the world's largest delivery companies, with a long-established UK operation focused on international express and B2B logistics. UK customers most often interact with UPS for inbound shipments from the US and EU.",
    },
    tags: tagsForCategory.logistics,
    takeaways: tk("courier", "customs brokerage charges and delivery scheduling"),
  },

  fedex: {
    slug: "fedex",
    name: "FedEx",
    url: "www.fedex.com/en-gb",
    category: "Transportation & Logistics",
    color: "#4D148C",
    initials: "FX",
    rating: 2.3,
    totalReviews: 3890,
    ratedReviews: 3890,
    recommend: 30,
    negative: 58,
    positive: 28,
    updatedOn: "05/23/2026",
    ratingBreakdown: { 5: 680, 4: 510, 3: 540, 2: 700, 1: 1460 },
    breadcrumbCategory: ["Transportation and Logistics", "/browse/transportation"],
    details: {
      legalName: "Federal Express Europe Inc. (UK branch)",
      industry: "Transportation & Logistics — International Express",
      founded: "1971 (UK operations from 1985)",
      headquarters: "Stansted Airport, Diamond Hangar, Essex, CM24 1RY",
      ceo: "Raj Subramaniam (Global)",
      employees: "6,500+ in the UK",
      parcelsPerYear: "Approximately 3 billion globally",
      website: "https://www.fedex.com/en-gb",
      trackingUrl: "https://www.fedex.com/fedextrack",
      customerSupport: {
        phone: "+44 345 607 0809",
        hours: "Mon–Fri 7:30–19:00 · Sat 9:00–13:00",
        chat: "Chat via fedex.com/en-gb/customer-support",
        email: "ukcustomer.service@fedex.com",
        twitter: "@FedExHelpEU",
        facebook: "facebook.com/FedEx",
      },
      postalAddress: "FedEx UK, PO Box 488, Diamond Hangar, Stansted, CM24 1RY",
      services: [
        "International Priority & Economy",
        "Domestic UK overnight",
        "FedEx Ground freight",
        "Returns & customs brokerage",
        "TNT-branded European road network",
      ],
      popularSearches: [
        "FedEx tracking UK", "FedEx customs charge", "FedEx UK contact number",
        "FedEx held parcel", "FedEx delivery missed", "FedEx vs TNT",
      ],
      introBlurb: "FedEx Express UK is the British arm of Federal Express, focused on international and overnight domestic shipments. Following the integration of TNT, FedEx also operates an extensive European road network.",
    },
    tags: tagsForCategory.logistics,
    takeaways: tk("courier", "customs delays and missed Priority deadlines"),
  },

  "british-gas": {
    slug: "british-gas",
    name: "British Gas",
    url: "www.britishgas.co.uk",
    category: "Utilities",
    color: "#1B5DAA",
    initials: "BG",
    rating: 1.4,
    totalReviews: 5610,
    ratedReviews: 5610,
    recommend: 8,
    negative: 86,
    positive: 6,
    updatedOn: "05/22/2026",
    ratingBreakdown: { 5: 280, 4: 220, 3: 380, 2: 720, 1: 4010 },
    breadcrumbCategory: ["Utilities", "/browse/utilities"],
    details: {
      legalName: "British Gas Services Ltd (Centrica plc)",
      industry: "Utilities — Energy Supply & Home Services",
      founded: "1812 (as Gas Light & Coke Company)",
      headquarters: "Millstream, Maidenhead Road, Windsor, SL4 5GD",
      ceo: "Chris O'Shea (Centrica)",
      employees: "20,000+",
      parcelsPerYear: "9 million household customers",
      website: "https://www.britishgas.co.uk",
      trackingUrl: "https://www.britishgas.co.uk/your-account",
      customerSupport: {
        phone: "+44 333 202 9802",
        hours: "Mon–Fri 9:00–17:00",
        chat: "Live chat via britishgas.co.uk/help",
        email: "customerservices@britishgas.co.uk",
        twitter: "@BritishGasHelp",
        facebook: "facebook.com/BritishGas",
      },
      postalAddress: "British Gas Customer Services, PO Box 226, Rotherham, S98 1PB",
      services: [
        "Gas and electricity supply",
        "Smart meter installation",
        "HomeCare boiler cover & repairs",
        "Heat pump installation",
        "Solar & EV charger packages",
      ],
      popularSearches: [
        "British Gas smart meter", "British Gas bill complaint", "British Gas tariff",
        "British Gas boiler repair", "Cancel British Gas HomeCare", "British Gas ombudsman",
      ],
      introBlurb: "British Gas is the UK's largest energy supplier, owned by Centrica plc, serving approximately 9 million household and business accounts. It also provides home-services products including the HomeCare boiler and heating cover plans.",
    },
    tags: tagsForCategory.utilities,
    takeaways: tk("energy", "billing errors and engineer no-shows"),
  },

  ee: {
    slug: "ee",
    name: "EE",
    url: "www.ee.co.uk",
    category: "Telecoms",
    color: "#00B5B0",
    initials: "EE",
    rating: 2.0,
    totalReviews: 4321,
    ratedReviews: 4321,
    recommend: 24,
    negative: 66,
    positive: 22,
    updatedOn: "05/21/2026",
    ratingBreakdown: { 5: 540, 4: 460, 3: 580, 2: 870, 1: 1871 },
    breadcrumbCategory: ["Telecoms", "/browse/telecoms"],
    details: {
      legalName: "EE Limited (BT Group plc)",
      industry: "Telecoms — Mobile & Broadband",
      founded: "2010 (merger of Orange and T-Mobile)",
      headquarters: "1 Braham Street, London, E1 8EE",
      ceo: "Marc Allera",
      employees: "13,000+",
      parcelsPerYear: "21 million mobile and broadband customers",
      website: "https://www.ee.co.uk",
      trackingUrl: "https://my.ee.co.uk",
      customerSupport: {
        phone: "150 (from EE mobile) · +44 7953 966 250",
        hours: "Mon–Fri 8:00–21:00 · Sat–Sun 8:00–20:00",
        chat: "MyEE app chat available 24/7",
        email: "Submitted via account messaging",
        twitter: "@EE",
        facebook: "facebook.com/ee",
      },
      postalAddress: "EE Customer Services, PO Box 6075, London, E1W 1JQ",
      services: [
        "Pay monthly mobile contracts",
        "Pay as you go SIMs",
        "Full-fibre 'Fibre to the Premises' broadband",
        "5G home broadband",
        "Apple TV+ and BT Sport bundles",
      ],
      popularSearches: [
        "EE bill query", "EE signal problems", "EE upgrade", "EE broadband speed",
        "Cancel EE contract", "EE international roaming",
      ],
      introBlurb: "EE is the UK's largest mobile network operator and a major broadband provider, owned by BT Group since 2016. It led the UK 5G rollout and bundles together mobile, broadband and TV products under the EE brand.",
    },
    tags: tagsForCategory.telecoms,
    takeaways: tk("telco", "mid-contract price rises and signal issues"),
  },

  sky: {
    slug: "sky",
    name: "Sky",
    url: "www.sky.com",
    category: "Telecoms / TV",
    color: "#0072C6",
    initials: "SK",
    rating: 1.9,
    totalReviews: 6890,
    ratedReviews: 6890,
    recommend: 19,
    negative: 71,
    positive: 16,
    updatedOn: "05/20/2026",
    ratingBreakdown: { 5: 600, 4: 480, 3: 730, 2: 1280, 1: 3800 },
    breadcrumbCategory: ["Telecoms / TV", "/browse/telecoms"],
    details: {
      legalName: "Sky UK Limited (Comcast Corporation)",
      industry: "Telecoms / TV — Subscription TV & Broadband",
      founded: "1989 (as Sky Television plc)",
      headquarters: "Grant Way, Isleworth, Middlesex, TW7 5QD",
      ceo: "Dana Strong",
      employees: "26,000+",
      parcelsPerYear: "Approximately 13 million UK customers",
      website: "https://www.sky.com",
      trackingUrl: "https://www.sky.com/help",
      customerSupport: {
        phone: "+44 333 759 4151",
        hours: "Mon–Fri 8:30–19:30 · Sat 8:30–18:00 · Sun 9:00–17:00",
        chat: "Live chat via sky.com/help",
        email: "Account messaging only",
        twitter: "@SkyHelpTeam",
        facebook: "facebook.com/sky",
      },
      postalAddress: "Sky Customer Services, PO Box 43, Livingston, EH54 7DD",
      services: [
        "Sky Q, Sky Glass and Sky Stream",
        "Sky Sports & Sky Cinema",
        "Sky Mobile",
        "Sky Broadband and Sky Talk",
        "Now TV streaming",
      ],
      popularSearches: [
        "Sky bill increase", "Cancel Sky contract", "Sky Q router problems",
        "Sky Stream review", "Sky engineer appointment", "Sky retention deal",
      ],
      introBlurb: "Sky UK is a Comcast-owned media and telecommunications company providing TV, broadband, mobile and streaming services to roughly 13 million UK households. It is best known for Sky Sports, Sky Cinema and the Sky Q/Sky Glass platforms.",
    },
    tags: tagsForCategory.tv,
    takeaways: tk("TV/telco", "mid-contract price rises and engineer no-shows"),
  },

  "virgin-media": {
    slug: "virgin-media",
    name: "Virgin Media",
    url: "www.virginmedia.com",
    category: "Telecoms",
    color: "#E1001A",
    initials: "VM",
    rating: 1.5,
    totalReviews: 7012,
    ratedReviews: 7012,
    recommend: 10,
    negative: 82,
    positive: 9,
    updatedOn: "05/19/2026",
    ratingBreakdown: { 5: 400, 4: 320, 3: 530, 2: 1080, 1: 4682 },
    breadcrumbCategory: ["Telecoms", "/browse/telecoms"],
    details: {
      legalName: "Virgin Media O2 (VMED O2 UK Limited)",
      industry: "Telecoms — Broadband, TV & Mobile",
      founded: "2007 (merger of NTL, Telewest and Virgin Mobile)",
      headquarters: "500 Brook Drive, Reading, RG2 6UU",
      ceo: "Lutz Schüler",
      employees: "16,000+",
      parcelsPerYear: "Around 47 million UK customer connections",
      website: "https://www.virginmedia.com",
      trackingUrl: "https://my.virginmedia.com",
      customerSupport: {
        phone: "150 (Virgin mobile) · +44 345 454 1111",
        hours: "Mon–Fri 8:00–21:00 · Sat 8:00–18:00 · Sun 8:00–18:00",
        chat: "Live chat via virginmedia.com/help",
        email: "Account messaging only",
        twitter: "@virginmedia",
        facebook: "facebook.com/virginmedia",
      },
      postalAddress: "Virgin Media Customer Concerns, Sunderland, SR43 4AA",
      services: [
        "Cable broadband (Gig1, M500, M250…)",
        "Virgin TV 360 & Stream",
        "Virgin Mobile",
        "Landline and bundle deals",
        "Business connectivity",
      ],
      popularSearches: [
        "Virgin Media price rise", "Cancel Virgin Media contract", "Virgin Media outage",
        "Virgin Media hub 5", "Virgin Media disconnection refund", "Virgin Media retention",
      ],
      introBlurb: "Virgin Media is a UK cable broadband, TV and mobile operator, part of the Virgin Media O2 joint venture between Liberty Global and Telefónica. It serves around 5.7 million broadband customers and is the UK's largest cable network.",
    },
    tags: tagsForCategory.telecoms,
    takeaways: tk("telco", "outages and difficult cancellations"),
  },

  tesco: {
    slug: "tesco",
    name: "Tesco",
    url: "www.tesco.com",
    category: "Retail",
    color: "#005EB8",
    initials: "TS",
    rating: 2.4,
    totalReviews: 9234,
    ratedReviews: 9234,
    recommend: 38,
    negative: 50,
    positive: 36,
    updatedOn: "05/18/2026",
    ratingBreakdown: { 5: 1800, 4: 1500, 3: 1530, 2: 1900, 1: 2504 },
    breadcrumbCategory: ["Retail", "/browse/retail"],
    details: {
      legalName: "Tesco PLC",
      industry: "Retail — Grocery & General Merchandise",
      founded: "1919",
      headquarters: "Tesco House, Shire Park, Welwyn Garden City, AL7 1GA",
      ceo: "Ken Murphy",
      employees: "330,000+",
      parcelsPerYear: "1.2 million online grocery orders per week",
      website: "https://www.tesco.com",
      trackingUrl: "https://secure.tesco.com/account/en-GB/myorders",
      customerSupport: {
        phone: "+44 800 50 5555",
        hours: "Mon–Fri 8:00–22:00 · Sat 8:00–20:00 · Sun 9:00–18:00",
        chat: "Live chat via tesco.com/help",
        email: "customer.service@tesco.co.uk",
        twitter: "@Tesco",
        facebook: "facebook.com/tesco",
      },
      postalAddress: "Tesco Customer Services, PO Box 73, Baird Avenue, Dundee, DD1 9NF",
      services: [
        "Grocery home delivery and Click+Collect",
        "Clubcard rewards programme",
        "F&F clothing",
        "Tesco Bank and Mobile",
        "Express, Metro, Superstore and Extra formats",
      ],
      popularSearches: [
        "Tesco delivery slot", "Tesco Clubcard problem", "Tesco missing items",
        "Tesco refund", "Tesco voucher", "Tesco delivery saver",
      ],
      introBlurb: "Tesco PLC is the United Kingdom's largest grocery retailer and one of the largest companies on the London Stock Exchange. It serves millions of customers in stores and online each week and operates one of the most-used loyalty programmes in the country (Clubcard).",
    },
    tags: tagsForCategory.retail,
    takeaways: tk("supermarket", "online order substitutions and Clubcard issues"),
  },

  asda: {
    slug: "asda",
    name: "Asda",
    url: "www.asda.com",
    category: "Retail",
    color: "#7BC143",
    initials: "AS",
    rating: 2.2,
    totalReviews: 5612,
    ratedReviews: 5612,
    recommend: 30,
    negative: 55,
    positive: 28,
    updatedOn: "05/17/2026",
    ratingBreakdown: { 5: 850, 4: 720, 3: 920, 2: 1230, 1: 1892 },
    breadcrumbCategory: ["Retail", "/browse/retail"],
    details: {
      legalName: "Asda Stores Limited (TDR Capital / Mohsin & Zuber Issa)",
      industry: "Retail — Grocery & General Merchandise",
      founded: "1949 (as Associated Dairies)",
      headquarters: "Great Wilson Street, Leeds, LS11 5AD",
      ceo: "Allan Leighton (Chairman) / Mohsin Issa (CEO)",
      employees: "150,000+",
      parcelsPerYear: "800,000 online grocery orders per week",
      website: "https://www.asda.com",
      trackingUrl: "https://groceries.asda.com/my-orders",
      customerSupport: {
        phone: "+44 800 952 0101",
        hours: "Mon–Fri 8:00–20:00 · Sat–Sun 9:00–18:00",
        chat: "Asda Live Chat via help.asda.com",
        email: "feedback@asda.co.uk",
        twitter: "@AsdaServiceTeam",
        facebook: "facebook.com/Asda",
      },
      postalAddress: "Asda Customer Services, Great Wilson Street, Leeds, LS11 5AD",
      services: [
        "Online grocery delivery and Click+Collect",
        "George at Asda clothing",
        "Asda Rewards app",
        "Asda Mobile",
        "Photo printing services",
      ],
      popularSearches: [
        "Asda delivery slot", "Asda Rewards pounds", "Asda George return",
        "Asda missing items", "Asda refund", "Asda price guarantee",
      ],
      introBlurb: "Asda is one of the largest supermarket chains in the United Kingdom, operating over 1,000 stores plus a major online grocery business. Following its 2021 buyout it expanded into convenience stores under the Asda Express format.",
    },
    tags: tagsForCategory.retail,
    takeaways: tk("supermarket", "missing items and Rewards app problems"),
  },

  "amazon-uk": {
    slug: "amazon-uk",
    name: "Amazon UK",
    url: "www.amazon.co.uk",
    category: "Retail",
    color: "#FF9900",
    initials: "AM",
    rating: 2.8,
    totalReviews: 21430,
    ratedReviews: 21430,
    recommend: 52,
    negative: 38,
    positive: 50,
    updatedOn: "05/16/2026",
    ratingBreakdown: { 5: 6500, 4: 3800, 3: 3200, 2: 2900, 1: 5030 },
    breadcrumbCategory: ["Retail", "/browse/retail"],
    details: {
      legalName: "Amazon UK Services Ltd",
      industry: "Retail — Online Marketplace",
      founded: "1998 (UK launch)",
      headquarters: "1 Principal Place, Worship Street, London, EC2A 2FA",
      ceo: "John Boumphrey (UK Country Manager)",
      employees: "75,000+ in the UK",
      parcelsPerYear: "Hundreds of millions of UK shipments",
      website: "https://www.amazon.co.uk",
      trackingUrl: "https://www.amazon.co.uk/orders",
      customerSupport: {
        phone: "+44 800 279 7234",
        hours: "24/7",
        chat: "24/7 chat in 'Help' inside your Amazon account",
        email: "Amazon escalates via the account message centre",
        twitter: "@AmazonHelp",
        facebook: "facebook.com/AmazonUK",
      },
      postalAddress: "Amazon UK Services, 1 Principal Place, London, EC2A 2FA",
      services: [
        "Retail marketplace (1st & 3rd-party sellers)",
        "Amazon Prime, Prime Video, Music",
        "Same-day & next-day Prime delivery",
        "Amazon Fresh grocery",
        "Kindle, Echo and Fire devices",
      ],
      popularSearches: [
        "Amazon UK return", "Amazon order missing", "Prime cancel",
        "Amazon refund issue", "Amazon seller complaint", "Amazon Fresh delivery",
      ],
      introBlurb: "Amazon UK is the British arm of Amazon.com, Inc. and the largest online retailer in the country by gross merchandise value. It also operates Prime Video, Amazon Fresh and the Kindle/Echo device ecosystems for UK customers.",
    },
    tags: tagsForCategory.retail,
    takeaways: tk("retailer", "missing parcels and third-party seller refund delays"),
  },

  asos: {
    slug: "asos",
    name: "ASOS",
    url: "www.asos.com",
    category: "Fashion",
    color: "#000000",
    initials: "AS",
    rating: 2.6,
    totalReviews: 8231,
    ratedReviews: 8231,
    recommend: 42,
    negative: 46,
    positive: 40,
    updatedOn: "05/15/2026",
    ratingBreakdown: { 5: 1900, 4: 1400, 3: 1320, 2: 1380, 1: 2231 },
    breadcrumbCategory: ["Fashion", "/browse/fashion"],
    details: {
      legalName: "ASOS PLC",
      industry: "Fashion — Online Apparel Retail",
      founded: "2000",
      headquarters: "Greater London House, Hampstead Road, London, NW1 7FB",
      ceo: "José Antonio Ramos Calamonte",
      employees: "3,500+",
      parcelsPerYear: "Approximately 65 million shipments",
      website: "https://www.asos.com",
      trackingUrl: "https://www.asos.com/my-account/orders",
      customerSupport: {
        phone: "ASOS does not operate a phone line",
        hours: "Chat 24/7 via asos.com/customer-care",
        chat: "24/7 chat & social DMs",
        email: "customercare@asos.com",
        twitter: "@ASOS_HeretoHelp",
        facebook: "facebook.com/ASOS",
      },
      postalAddress: "ASOS Customer Care, Greater London House, Hampstead Road, NW1 7FB",
      services: [
        "Womenswear, menswear, beauty",
        "ASOS Marketplace (third-party sellers)",
        "Premier Delivery subscription",
        "28-day free returns",
        "Topshop, Topman & Miss Selfridge brands",
      ],
      popularSearches: [
        "ASOS return refund", "ASOS Premier", "ASOS order missing",
        "ASOS account blocked", "ASOS size guide", "ASOS discount code",
      ],
      introBlurb: "ASOS PLC is a UK-headquartered online fashion retailer focused on customers in their twenties, selling its own labels alongside thousands of branded products. It is one of the largest pure-play fashion retailers in the world.",
    },
    tags: tagsForCategory.fashion,
    takeaways: tk("fashion retailer", "delayed refunds and account blocks after frequent returns"),
  },

  boots: {
    slug: "boots",
    name: "Boots",
    url: "www.boots.com",
    category: "Retail",
    color: "#005EB8",
    initials: "BO",
    rating: 2.5,
    totalReviews: 3120,
    ratedReviews: 3120,
    recommend: 36,
    negative: 52,
    positive: 32,
    updatedOn: "05/14/2026",
    ratingBreakdown: { 5: 580, 4: 470, 3: 480, 2: 620, 1: 970 },
    breadcrumbCategory: ["Retail", "/browse/retail"],
    details: {
      legalName: "Boots UK Limited (Walgreens Boots Alliance)",
      industry: "Retail — Health & Beauty / Pharmacy",
      founded: "1849",
      headquarters: "Thane Road, Nottingham, NG90 1BS",
      ceo: "Anthony Hemmerdinger (Boots UK MD)",
      employees: "55,000+",
      parcelsPerYear: "Over 1,800 UK stores plus boots.com",
      website: "https://www.boots.com",
      trackingUrl: "https://www.boots.com/account/orders",
      customerSupport: {
        phone: "+44 345 070 8090",
        hours: "Mon–Sat 8:00–22:00 · Sun 9:00–18:00",
        chat: "Boots Live Chat via boots.com/help",
        email: "customer.care@boots.co.uk",
        twitter: "@BootsHelp",
        facebook: "facebook.com/bootsuk",
      },
      postalAddress: "Boots Customer Care, PO Box 5300, Nottingham, NG90 1AA",
      services: [
        "High-street and online retail of beauty, healthcare and baby items",
        "NHS and private prescription pharmacy services",
        "Boots Opticians",
        "Advantage Card loyalty programme",
        "No7, Soap & Glory and other own brands",
      ],
      popularSearches: [
        "Boots Advantage Card balance", "Boots online order missing", "Boots prescription",
        "Boots return policy", "Boots offers", "Boots eye test",
      ],
      introBlurb: "Boots UK is the country's largest pharmacy chain, operating over 1,800 stores and a substantial online business. Part of Walgreens Boots Alliance, it sits at the heart of UK community pharmacy services.",
    },
    tags: tagsForCategory.retail,
    takeaways: tk("retailer", "Advantage Card points and online order issues"),
  },

  argos: {
    slug: "argos",
    name: "Argos",
    url: "www.argos.co.uk",
    category: "Retail",
    color: "#ED1C24",
    initials: "AR",
    rating: 2.3,
    totalReviews: 4567,
    ratedReviews: 4567,
    recommend: 31,
    negative: 60,
    positive: 28,
    updatedOn: "05/13/2026",
    ratingBreakdown: { 5: 620, 4: 510, 3: 540, 2: 900, 1: 1997 },
    breadcrumbCategory: ["Retail", "/browse/retail"],
    details: {
      legalName: "Argos Limited (Sainsbury's Group)",
      industry: "Retail — General Merchandise",
      founded: "1972",
      headquarters: "489–499 Avebury Boulevard, Milton Keynes, MK9 2NW",
      ceo: "Andrea Murphy",
      employees: "23,000+",
      parcelsPerYear: "Over 100 million transactions per year",
      website: "https://www.argos.co.uk",
      trackingUrl: "https://www.argos.co.uk/help/orders",
      customerSupport: {
        phone: "+44 345 640 2020",
        hours: "Mon–Sat 8:00–22:00 · Sun 10:00–18:00",
        chat: "Chat via argos.co.uk/help",
        email: "customerservices@argos.co.uk",
        twitter: "@ArgosHelpers",
        facebook: "facebook.com/Argos",
      },
      postalAddress: "Argos Customer Services, 489 Avebury Boulevard, Milton Keynes, MK9 2NW",
      services: [
        "Same-day Fast Track collection in store",
        "Home delivery (next-day and standard)",
        "Toys, electronics, homewares and furniture",
        "Nectar loyalty programme",
        "Argos inside Sainsbury's stores",
      ],
      popularSearches: [
        "Argos Fast Track", "Argos missing item", "Argos refund",
        "Argos delivery driver", "Argos warranty", "Argos store finder",
      ],
      introBlurb: "Argos is a UK general-merchandise retailer owned by Sainsbury's. It is best known for its catalogue-and-collect format, which has since evolved into an omnichannel model with same-day collection and home delivery.",
    },
    tags: tagsForCategory.retail,
    takeaways: tk("retailer", "missing furniture deliveries and warranty disputes"),
  },

  currys: {
    slug: "currys",
    name: "Currys",
    url: "www.currys.co.uk",
    category: "Retail",
    color: "#4C0E92",
    initials: "CU",
    rating: 1.8,
    totalReviews: 3987,
    ratedReviews: 3987,
    recommend: 18,
    negative: 72,
    positive: 16,
    updatedOn: "05/12/2026",
    ratingBreakdown: { 5: 320, 4: 290, 3: 410, 2: 720, 1: 2247 },
    breadcrumbCategory: ["Retail", "/browse/retail"],
    details: {
      legalName: "Currys plc",
      industry: "Retail — Electricals",
      founded: "1884",
      headquarters: "1 Portal Way, Acton, London, W3 6RS",
      ceo: "Alex Baldock",
      employees: "28,000+",
      parcelsPerYear: "Over 300 stores plus currys.co.uk",
      website: "https://www.currys.co.uk",
      trackingUrl: "https://www.currys.co.uk/orders",
      customerSupport: {
        phone: "+44 344 561 1234",
        hours: "Mon–Fri 8:00–20:00 · Sat 9:00–18:00 · Sun 10:00–17:00",
        chat: "Chat via currys.co.uk/help-and-support",
        email: "customer.care@currys.co.uk",
        twitter: "@curryshelp",
        facebook: "facebook.com/CurrysUK",
      },
      postalAddress: "Currys Customer Care, PO Box 1685, Sheffield, S2 5YJ",
      services: [
        "Home appliance and electronics sales",
        "ShopLive video shopping advisor",
        "Care & Repair plans",
        "Recycling and trade-in",
        "Same-day delivery and installation",
      ],
      popularSearches: [
        "Currys installation", "Currys Care & Repair", "Currys delivery driver",
        "Currys refund", "Currys order missing", "Currys ShopLive",
      ],
      introBlurb: "Currys plc is the United Kingdom's largest electricals retailer, formed from the merger of Dixons Carphone brands. It sells appliances and consumer electronics through over 300 stores and a major e-commerce operation.",
    },
    tags: tagsForCategory.retail,
    takeaways: tk("electricals retailer", "damaged appliance deliveries and Care & Repair disputes"),
  },
};

export function getCompanyBySlug(slug) {
  return COMPANIES[slug] || null;
}

export const COMPANY_LIST = Object.values(COMPANIES);
